import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import type { Triage } from '@cortex/domain-team-meeting';
import {
  useDeleteTeamMeetingPost,
  useReopenTeamMeetingPost,
  useToggleTeamMeetingLike,
  useUpdateTeamMeetingPost,
} from '../../../hooks/useTeamMeetings.js';
import type { OrgIssuePeople } from '../../../lib/api/org-issues.js';
import type { PostView } from '../../../lib/api/team-meetings.js';
import { personLabel } from '../../../lib/org-issues.js';
import { STICKY_CLASS, stickyStyle } from './StickyNote.js';

/** ネクストアクションを決めるポップアップを開く口。 @graph-connects none */
export interface TriageHandlers {
  toRequest: () => void;
  /** 既定のボードが無いシリーズでは起票の先が決まらないので `null`（ボタンを出さない） */
  toIssue: (() => void) | null;
  close: () => void;
}

/** @graph-connects none */
interface PostCardProps {
  view: PostView;
  people: OrgIssuePeople;
  viewerId: string | null;
  onTriage: TriageHandlers;
}

/**
 * 決めたネクストアクション（棚卸し済みの投稿に出す）。
 * @graph-connects none
 */
function TriageResult({ triage, people }: { triage: Triage; people: OrgIssuePeople }): ReactNode {
  const { t } = useTranslation();
  const { nextAction } = triage;
  const by = t('teamMeetings.triage.by', { who: personLabel(people, triage.by) });
  return (
    <div className="space-y-1 rounded bg-[var(--color-bg-hover)] p-2 text-xs">
      {nextAction.kind === 'pilab_request' && (
        <Link to="/improvements">
          <Badge variant="info" size="sm">
            {t('teamMeetings.triage.result.pilab_request')}
          </Badge>
        </Link>
      )}
      {nextAction.kind === 'org_issue' && (
        <Link
          to="/org-issues/$boardId"
          params={{ boardId: nextAction.boardId }}
          search={{ issue: nextAction.issueId }}
        >
          <Badge variant="success" size="sm">
            {t('teamMeetings.triage.result.org_issue')}
          </Badge>
        </Link>
      )}
      {nextAction.kind === 'closed' && (
        <>
          <Badge variant="default" size="sm">
            {t('teamMeetings.triage.result.closed')}
          </Badge>
          <p className="whitespace-pre-wrap text-[var(--color-text-secondary)]">
            {nextAction.note}
          </p>
        </>
      )}
      <p className="text-[var(--color-text-muted)]">{by}</p>
    </div>
  );
}

/** 書き換えている欄。 @graph-connects none */
type Editing = 'none' | 'body' | 'note';

/**
 * 投稿 1 枚。いいね・議論メモ・本文の書き換え・削除と、リーダー以上にはネクストアクション
 * （チーム課題にする・改善要望にする・対応を書いてクローズ）を置く。棚卸し済みなら決めたことを出す。
 * 出し分けは API が返す `permissions` だけで決める（本人・リーダーかどうかを画面で判定しない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿 1 枚に、いいね・議論メモ・本文の書き換え・削除・ネクストアクションの決定・棚卸しを戻すを置き、決めたことを出す
 * @graph-connects useToggleTeamMeetingLike [calls] いいね
 * @graph-connects useUpdateTeamMeetingPost [calls] 本文・議論メモの書き換え
 * @graph-connects useDeleteTeamMeetingPost [calls] 削除
 * @graph-connects useReopenTeamMeetingPost [calls] 棚卸しを戻す
 */
export function PostCard({ view, people, viewerId, onTriage }: PostCardProps): ReactNode {
  const { t } = useTranslation();
  const { post, permissions } = view;
  const ref = { seriesId: post.seriesId, postId: post.id };
  const like = useToggleTeamMeetingLike();
  const update = useUpdateTeamMeetingPost();
  const remove = useDeleteTeamMeetingPost();
  const reopen = useReopenTeamMeetingPost();
  const [editing, setEditing] = useState<Editing>('none');
  const [draft, setDraft] = useState('');
  const liked = viewerId !== null && post.likedBy.includes(viewerId);
  const error = [like, update, remove, reopen].find((m) => m.isError)?.error;

  const startEditing = (field: Exclude<Editing, 'none'>) => {
    setDraft(field === 'body' ? post.body : post.discussionNote);
    setEditing(field);
  };
  const save = () => {
    update.mutate(
      { ref, patch: editing === 'body' ? { body: draft } : { discussionNote: draft } },
      {
        onSuccess: () => {
          setEditing('none');
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <div
        className={`${STICKY_CLASS} w-full min-h-[180px] text-sm`}
        style={stickyStyle(post.authorId)}
      >
        {editing === 'body' ? (
          <textarea
            rows={8}
            className="w-full flex-1 resize-y bg-white/50 p-1 outline-none"
            aria-label={t('teamMeetings.post.body')}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
          />
        ) : (
          <p className="flex-1 whitespace-pre-wrap break-words">{post.body}</p>
        )}
        <p className="mt-3 text-xs text-[#1e1e1e]/60">{personLabel(people, post.authorId)}</p>
      </div>

      {editing === 'note' ? (
        <Textarea
          rows={2}
          aria-label={t('teamMeetings.post.discussionNote')}
          placeholder={t('teamMeetings.post.discussionNotePlaceholder')}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
        />
      ) : (
        post.discussionNote !== '' && (
          <p className="whitespace-pre-wrap rounded bg-[var(--color-bg-hover)] p-2 text-xs text-[var(--color-text-secondary)]">
            {post.discussionNote}
          </p>
        )
      )}

      {post.triage !== null && <TriageResult triage={post.triage} people={people} />}

      {error !== undefined && (
        <p className="whitespace-pre-wrap text-xs text-[var(--color-error)]" role="alert">
          {error.message}
        </p>
      )}

      {permissions.canTriage && editing === 'none' && (
        <div
          className="flex flex-wrap items-center gap-1 border-t border-[var(--color-border)] pt-2"
          aria-label={t('teamMeetings.triage.heading')}
          role="group"
        >
          {onTriage.toIssue !== null && (
            <Button type="button" size="sm" variant="secondary" onClick={onTriage.toIssue}>
              {t('teamMeetings.triage.toIssue')}
            </Button>
          )}
          <Button type="button" size="sm" variant="secondary" onClick={onTriage.toRequest}>
            {t('teamMeetings.triage.toRequest')}
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onTriage.close}>
            {t('teamMeetings.triage.closeOpen')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1">
        {editing === 'none' ? (
          <>
            <Button
              type="button"
              size="sm"
              variant={liked ? 'primary' : 'ghost'}
              disabled={!permissions.canDiscuss}
              loading={like.isPending}
              title={post.likedBy.map((id) => personLabel(people, id)).join(', ')}
              onClick={() => {
                like.mutate(ref);
              }}
            >
              {t('teamMeetings.post.like', { count: post.likedBy.length })}
            </Button>
            {permissions.canDiscuss && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  startEditing('note');
                }}
              >
                {t('teamMeetings.post.editNote')}
              </Button>
            )}
            {permissions.canEditBody && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  startEditing('body');
                }}
              >
                {t('teamMeetings.post.edit')}
              </Button>
            )}
            {permissions.canDelete && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                loading={remove.isPending}
                onClick={() => {
                  if (window.confirm(t('teamMeetings.post.deleteConfirm'))) remove.mutate(ref);
                }}
              >
                {t('teamMeetings.post.delete')}
              </Button>
            )}
            {permissions.canReopen && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                loading={reopen.isPending}
                onClick={() => {
                  if (window.confirm(t('teamMeetings.triage.reopenConfirm'))) reopen.mutate(ref);
                }}
              >
                {t('teamMeetings.triage.reopen')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button type="button" size="sm" loading={update.isPending} onClick={save}>
              {t('teamMeetings.common.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing('none');
              }}
            >
              {t('teamMeetings.common.cancel')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
